import { useMutation, useQuery, useQueryClient } from 'react-query';

import { useServiceError } from '@/hooks';

import {
  ListSubGroupsQuery,
  SubGroup,
  UpdateSubGroupRequest,
} from './SubGroups.types';
import {
  createSubGroup,
  deleteSubGroup,
  listSubGroups,
  listSubGroupsByGroups,
  updateSubGroup,
} from './SubGroups.service';

import { DEFAULT_ORDER } from '..';
import { useIntl } from 'react-intl';
import { notification } from 'antd';
import notificationError from '@/utils/functions/notificationError';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';

export function useSubGroups(params?: ListSubGroupsQuery) {
  const query = useQuery(['subgroups', { ...params }], () => {
    const req: ListSubGroupsQuery = {
      order: params?.order || DEFAULT_ORDER,
      ...params,
    };

    return listSubGroups(req);
  });

  useServiceError(query.error);

  return query;
}

export function useSubGroupsMutation() {
  return useMutation(
    (params?: ListSubGroupsQuery) => {
      const req: ListSubGroupsQuery = {
        order: params?.order || DEFAULT_ORDER,
        ...params,
      };

      return listSubGroups(req);
    },
    {
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useSubGroupsQuery(groupId: number) {
  const query = useQuery(
    ['subgroups', groupId],
    () => {
      return listSubGroupsByGroups(groupId);
    },
    { enabled: hasPermissions([Authorities.REGISTER_SUBGROUP_VIEW]) },
  );

  useServiceError(query.error);

  return query;
}

export function useSubGroupByGroup() {
  const mutation = useMutation((groupId: number) =>
    listSubGroupsByGroups(groupId),
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useCreateSubGroup() {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  const mutation = useMutation((obj: SubGroup) => createSubGroup(obj), {
    async onSuccess() {
      queryChanel({
        resetQueries: [['groups'], ['subgroups']],
        invalidateQueries: [['groups'], ['subgroups']],
      });

      notification.success({
        message: intl.formatMessage({
          id: 'groups.subgroups.create.notification.success',
        }),
        description: intl.formatMessage({
          id: 'groups.subgroups.create.notification.successDescription',
        }),
      });
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            err.includes('SubGroup already exists')
              ? 'Subgrupo já existe.'
              : err.includes('Already a Sub Group with the name:')
              ? 'Já existe um subgrupo com esse nome.'
              : err,
          ),
      });
    },
  });

  return mutation;
}

export function useUpdateSubGroup() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (obj: UpdateSubGroupRequest) => updateSubGroup(obj),
    {
      onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'groups.subgroups.update.notification.success',
          }),
          description: intl.formatMessage({
            id: 'groups.subgroups.update.notification.successDescription',
          }),
        });
        queryClient.invalidateQueries(['subgroups']);
      },
      onError(error) {
        notificationError(error, {
          translate: (errors) =>
            errors.map((err) =>
              err.includes('Already a Sub Group with the name:')
                ? 'Já existe um subgrupo com esse nome.'
                : err,
            ),
        });
      },
    },
  );

  return mutation;
}

export function useDeleteSubGroup() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation((id: number) => deleteSubGroup(id), {
    onSuccess() {
      notification.success({
        message: intl.formatMessage({
          id: 'groups.subgroups.remove.notification.success',
        }),
        description: intl.formatMessage({
          id: 'groups.subgroups.remove.notification.successDescription',
        }),
      }),
        queryClient.invalidateQueries(['subgroups']);
    },
  });

  useServiceError(mutation.error);

  return mutation;
}
